<script setup lang="ts">
/**
 * Settings page — account, linked accounts, sessions, danger zone.
 *
 * Sections:
 *  1. Profile   – display name (PATCH /api/me), readonly userId + email.
 *  2. Linked accounts – Google / Telegram / Wallet status; disconnect allowed
 *     only when ≥ 2 providers remain linked.
 *  3. Active sessions – table from GET /api/me/sessions; refresh + logout-all.
 *  4. Danger zone – collapsed by default; typed DELETE modal to soft-delete account.
 *
 * All mutations show pending / error / success state locally.
 * After identity unlink or profile update the auth store is refreshed so the
 * sidebar and page re-render with the latest provider set.
 */
import { onMounted, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { meApi, type AuthProvider, type SettingsSessionRow } from '../api/me';
import { formatDateTime } from '../lib/format';

const auth = useAuthStore();
const router = useRouter();

// ── Section 1: Profile ───────────────────────────────────────────────────────
const displayNameInput = ref('');
const displayNamePending = ref(false);
const displayNameError = ref<string | null>(null);
const displayNameSuccess = ref(false);
const displayNameCopied = ref(false);

function syncDisplayName(): void {
  displayNameInput.value = auth.me?.displayName ?? '';
}

// Sync when auth.me becomes available.
watch(() => auth.me, syncDisplayName, { immediate: true });

async function saveDisplayName(): Promise<void> {
  const raw = displayNameInput.value.trim();
  const value = raw === '' ? null : raw;

  // Client-side length guard (backend enforces ≤ 64 chars).
  if (raw.length > 64) return;

  displayNamePending.value = true;
  displayNameError.value = null;
  displayNameSuccess.value = false;

  try {
    await auth.updateDisplayName(value);
    displayNameSuccess.value = true;
    setTimeout(() => {
      displayNameSuccess.value = false;
    }, 2500);
  } catch (err) {
    displayNameError.value = (err as Error).message;
  } finally {
    displayNamePending.value = false;
  }
}

async function copyUserId(): Promise<void> {
  if (!auth.me) return;
  await navigator.clipboard.writeText(auth.me.userId);
  displayNameCopied.value = true;
  setTimeout(() => {
    displayNameCopied.value = false;
  }, 1800);
}

// ── Section 2: Linked accounts ───────────────────────────────────────────────
const ALL_PROVIDERS: AuthProvider[] = ['google', 'telegram', 'wallet'];

const linkedProviders = computed(() => auth.me?.providers ?? []);

function isLinked(provider: AuthProvider): boolean {
  return linkedProviders.value.includes(provider);
}

const canDisconnect = computed(() => linkedProviders.value.length > 1);

function disconnectTooltip(provider: AuthProvider): string {
  if (!isLinked(provider)) return '';
  if (!canDisconnect.value) return 'At least one sign-in method must remain connected.';
  return `Disconnect ${providerLabel(provider)}`;
}

function providerLabel(p: AuthProvider): string {
  return { google: 'Google', telegram: 'Telegram', wallet: 'Wallet' }[p];
}

const disconnectPending = ref<AuthProvider | null>(null);
const disconnectError = ref<string | null>(null);
const disconnectSuccess = ref<AuthProvider | null>(null);

async function disconnect(provider: AuthProvider): Promise<void> {
  if (!canDisconnect.value) return;
  disconnectPending.value = provider;
  disconnectError.value = null;
  disconnectSuccess.value = null;

  try {
    await meApi.unlinkIdentity(provider);
    await auth.refresh();
    disconnectSuccess.value = provider;
    setTimeout(() => {
      disconnectSuccess.value = null;
    }, 2500);
  } catch (err) {
    disconnectError.value = (err as Error).message;
  } finally {
    disconnectPending.value = null;
  }
}

// ── Section 3: Active sessions ───────────────────────────────────────────────
const sessions = ref<SettingsSessionRow[]>([]);
const sessionsLoading = ref(false);
const sessionsError = ref<string | null>(null);
const logoutAllPending = ref(false);
const logoutAllError = ref<string | null>(null);
const logoutAllSuccess = ref<string | null>(null);

async function loadSessions(): Promise<void> {
  sessionsLoading.value = true;
  sessionsError.value = null;
  try {
    const res = await meApi.sessions();
    sessions.value = res.rows;
  } catch (err) {
    sessionsError.value = (err as Error).message;
  } finally {
    sessionsLoading.value = false;
  }
}

async function logoutAllSessions(): Promise<void> {
  logoutAllPending.value = true;
  logoutAllError.value = null;
  logoutAllSuccess.value = null;
  try {
    const res = await meApi.logoutAllSessions();
    logoutAllSuccess.value = `${res.logged_out_count} session(s) revoked.`;
    await loadSessions();
    setTimeout(() => {
      logoutAllSuccess.value = null;
    }, 3000);
  } catch (err) {
    logoutAllError.value = (err as Error).message;
  } finally {
    logoutAllPending.value = false;
  }
}

onMounted(loadSessions);

// ── Section 4: Danger zone ───────────────────────────────────────────────────
const deleteModalOpen = ref(false);
const deleteConfirmInput = ref('');
const deletePending = ref(false);
const deleteError = ref<string | null>(null);

const deleteConfirmValid = computed(() => deleteConfirmInput.value.trim() === 'DELETE');

function openDeleteModal(): void {
  deleteConfirmInput.value = '';
  deleteError.value = null;
  deleteModalOpen.value = true;
}

function closeDeleteModal(): void {
  deleteModalOpen.value = false;
}

async function confirmDelete(): Promise<void> {
  if (!deleteConfirmValid.value) return;
  deletePending.value = true;
  deleteError.value = null;

  try {
    await meApi.deleteAccount('DELETE');
    // Clear local auth state and redirect to login.
    await auth.logout();
    await router.push({ name: 'login' });
  } catch (err) {
    deleteError.value = (err as Error).message;
    deletePending.value = false;
  }
}
</script>

<template>
  <div class="px-6 lg:px-10 py-8 flex flex-col gap-6 max-w-3xl">

    <!-- Page header -->
    <header>
      <h1 class="text-2xl font-semibold text-ink">Settings</h1>
      <p class="text-sm text-muted mt-1">Account, sessions, and security.</p>
    </header>

    <!-- ── 1. Profile ─────────────────────────────────────────────────────── -->
    <section class="card p-6 flex flex-col gap-5">
      <h2 class="text-sm uppercase tracking-wide text-muted">Profile</h2>

      <!-- Display name -->
      <div class="flex flex-col gap-1.5">
        <label for="display-name" class="text-sm text-ink">Display name</label>
        <div class="flex gap-2 items-start flex-wrap">
          <input
            id="display-name"
            v-model="displayNameInput"
            type="text"
            class="input flex-1 min-w-0"
            placeholder="e. g. alice"
            maxlength="64"
            :disabled="displayNamePending"
          />
          <button
            type="button"
            class="btn-primary text-sm whitespace-nowrap"
            :disabled="displayNamePending || displayNameInput.length > 64"
            @click="saveDisplayName"
          >
            {{ displayNamePending ? 'Saving…' : 'Save' }}
          </button>
        </div>
        <p v-if="displayNameError" class="text-xs text-danger">{{ displayNameError }}</p>
        <p v-else-if="displayNameSuccess" class="text-xs text-ok">Saved.</p>
        <p class="text-xs text-muted">{{ displayNameInput.length }}/64 characters</p>
      </div>

      <!-- Email (readonly) -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm text-ink">Email</label>
        <div class="input bg-canvas text-muted select-all">
          {{ auth.me?.email ?? 'Not available' }}
        </div>
        <p class="text-xs text-muted">Email is managed by your identity providers.</p>
      </div>

      <!-- User ID (readonly + copy) -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm text-ink">User ID</label>
        <div class="flex gap-2 items-start flex-wrap">
          <div
            class="input flex-1 min-w-0 bg-canvas text-muted select-all font-mono text-xs overflow-hidden truncate"
            :title="auth.me?.userId ?? ''"
          >
            {{ auth.me?.userId ?? '—' }}
          </div>
          <button
            type="button"
            class="btn-ghost text-sm whitespace-nowrap"
            :disabled="!auth.me?.userId"
            @click="copyUserId"
          >
            {{ displayNameCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
      </div>
    </section>

    <!-- ── 2. Linked accounts ───────────────────────────────────────────── -->
    <section class="card p-6 flex flex-col gap-5">
      <h2 class="text-sm uppercase tracking-wide text-muted">Linked accounts</h2>

      <div v-if="disconnectError" class="text-xs text-danger">{{ disconnectError }}</div>

      <div class="flex flex-col gap-3">
        <div
          v-for="provider in ALL_PROVIDERS"
          :key="provider"
          class="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0"
        >
          <!-- Provider badge -->
          <div class="flex items-center gap-3">
            <span
              class="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold"
              :class="isLinked(provider) ? 'bg-surface-soft text-ok' : 'bg-code text-muted'"
            >
              {{ provider.charAt(0).toUpperCase() }}
            </span>
            <div>
              <div class="text-sm text-ink">{{ providerLabel(provider) }}</div>
              <div
                class="text-xs"
                :class="isLinked(provider) ? 'text-ok' : 'text-muted'"
              >
                {{ isLinked(provider) ? 'Connected' : 'Not connected' }}
              </div>
            </div>
          </div>

          <!-- Disconnect button -->
          <button
            v-if="isLinked(provider)"
            type="button"
            class="btn-ghost text-xs"
            :disabled="!canDisconnect || disconnectPending !== null"
            :title="disconnectTooltip(provider)"
            @click="disconnect(provider)"
          >
            <span v-if="disconnectPending === provider">Disconnecting…</span>
            <span v-else-if="disconnectSuccess === provider" class="text-ok">Disconnected</span>
            <span v-else>Disconnect</span>
          </button>
        </div>
      </div>

      <p v-if="!canDisconnect" class="text-xs text-muted">
        At least one sign-in method must remain connected.
      </p>
    </section>

    <!-- ── 3. Active sessions ────────────────────────────────────────────── -->
    <section class="card p-6 flex flex-col gap-5">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <h2 class="text-sm uppercase tracking-wide text-muted">Active sessions</h2>
        <div class="flex gap-2">
          <button
            type="button"
            class="btn-ghost text-xs"
            :disabled="sessionsLoading"
            @click="loadSessions"
          >
            {{ sessionsLoading ? 'Loading…' : 'Refresh' }}
          </button>
          <button
            type="button"
            class="btn-danger"
            :disabled="logoutAllPending"
            @click="logoutAllSessions"
          >
            {{ logoutAllPending ? 'Logging out…' : 'Log out all other sessions' }}
          </button>
        </div>
      </div>

      <div v-if="logoutAllError" class="text-xs text-danger">{{ logoutAllError }}</div>
      <div v-else-if="logoutAllSuccess" class="text-xs text-ok">
        {{ logoutAllSuccess }}
      </div>

      <div v-if="sessionsError" class="text-xs text-danger">{{ sessionsError }}</div>

      <div v-else-if="sessionsLoading && sessions.length === 0" class="text-sm text-muted">
        Loading…
      </div>

      <div
        v-else-if="sessions.length === 0"
        class="text-sm text-muted italic"
      >
        No active sessions.
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Last used</th>
              <th>User agent</th>
              <th>IP</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in sessions" :key="s.session_id">
              <td class="text-muted whitespace-nowrap">{{ formatDateTime(s.created_at) }}</td>
              <td class="text-muted whitespace-nowrap">{{ formatDateTime(s.last_used_at) }}</td>
              <td
                class="text-muted text-xs max-w-xs truncate"
                :title="s.user_agent ?? ''"
              >
                {{ s.user_agent ?? '—' }}
              </td>
              <td class="text-muted text-xs">{{ s.ip ?? '—' }}</td>
              <td>
                <span
                  v-if="s.is_current"
                  class="inline-flex items-center text-[11px] font-medium tracking-wide uppercase px-2.5 py-1 rounded-md border bg-surface-soft/60 border-ok/40 text-ok"
                >
                  Current
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ── 4. Danger zone ────────────────────────────────────────────────── -->
    <details class="card p-6 flex flex-col gap-4 border border-danger/30">
      <summary class="flex items-center justify-between gap-3 flex-1 cursor-pointer list-none select-none">
        <h2 class="text-sm uppercase tracking-wide text-danger">Danger zone</h2>
        <span class="text-muted text-xs">▼</span>
      </summary>

      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div class="text-sm text-ink">Delete account</div>
          <div class="text-xs text-muted mt-0.5">
            Permanently remove your account and all associated data.
            This cannot be undone.
          </div>
        </div>
        <button
          type="button"
          class="btn-danger"
          @click="openDeleteModal"
        >
          Delete account
        </button>
      </div>
    </details>

    <!-- Delete account modal -->
    <Teleport to="body">
      <div
        v-if="deleteModalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/70"
          @click="closeDeleteModal"
        />

        <!-- Dialog -->
        <div class="relative z-10 card p-6 w-full max-w-md flex flex-col gap-5">
          <div>
            <h3 id="delete-modal-title" class="text-lg font-semibold text-ink">
              Delete account
            </h3>
            <p class="text-sm text-muted mt-1">
              This will permanently delete your account, revoke all sessions and API
              keys, and remove all associated data. <strong class="text-ink">This cannot be undone.</strong>
            </p>
          </div>

          <div class="flex flex-col gap-1.5">
            <label for="delete-confirm" class="text-sm text-ink">
              Type <code class="font-mono text-xs">DELETE</code> to confirm:
            </label>
            <input
              id="delete-confirm"
              v-model="deleteConfirmInput"
              type="text"
              class="input w-full"
              placeholder="DELETE"
              autocomplete="off"
              :disabled="deletePending"
            />
          </div>

          <div v-if="deleteError" class="text-xs text-danger">{{ deleteError }}</div>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="btn-ghost text-sm"
              :disabled="deletePending"
              @click="closeDeleteModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn-danger text-sm"
              :disabled="!deleteConfirmValid || deletePending"
              @click="confirmDelete"
            >
              {{ deletePending ? 'Deleting…' : 'Delete my account' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>